/**
 * The pool's Results tab: the standings board and, beside it, the Picks view.
 * Addressed at `/pools?poolId=<id>&tab=results` with an optional
 * `&contestId=<id>` and `&panel=picks`.
 *
 * Two reads drive the board: `POST /contest/list` to resolve which contest it
 * shows when the URL does not say, and `POST /standings/get` for the board
 * itself. `/standings/get` takes no cursor or limit and returns the whole
 * board in one pass, so every row is rendered with no pagination.
 *
 * On top of the board (2026-11-01 parity batch, after the legacy Results tab):
 *
 *  - ISSUE-1318. Any row opens that member's picks in a sheet, read from
 *    `POST /results/get-by-member`, which only ever returns locked picks.
 *  - ISSUE-1317. A Standings or Picks switch. Picks lists who picked what in
 *    one round, from `POST /results/get-by-round`.
 *  - ISSUE-1327 and ISSUE-1348. Status filter chips and sortable column
 *    headers, both over the rows already loaded. The rank shown is always the
 *    server's.
 *  - ISSUE-1328. The Commissioner's "Still to pick" card with a Nudge per
 *    member.
 *  - ISSUE-1331. A contest menu once the season holds more than one contest,
 *    so an earlier contest's board and picks stay one tap away.
 */
import {
  useContestList,
  useStandingsGet,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  cn,
  InlineBanner,
  SegmentedControl,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { extractErrorCode } from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import { SectionEmptyState } from "../../shell/SectionScreen";
import { NEW_CONTEST_START_ACTION_LABEL } from "../newContest/newContestModel";
import { MemberRoundsSheet } from "./MemberRoundsSheet";
import {
  applyStandingsSort,
  buildContestOptions,
  buildStandingsRowModels,
  DEFAULT_STANDINGS_SORT,
  filterStandingsRows,
  hasAnyRevive,
  nextStandingsSort,
  resolveStandingsPhase,
  selectStandingsContestId,
  STANDINGS_BACK_LABEL,
  STANDINGS_CONTEST_LABEL,
  STANDINGS_EMPTY_BODY,
  STANDINGS_EMPTY_HEADLINE,
  STANDINGS_FAILED_MESSAGE,
  STANDINGS_FILTER_GROUP_LABEL,
  STANDINGS_LOADING_LABEL,
  STANDINGS_NO_CONTEST_BODY,
  STANDINGS_NO_CONTEST_HEADLINE,
  STANDINGS_RETRY_LABEL,
  STANDINGS_SORT_GROUP_LABEL,
  STANDINGS_SORT_LABELS,
  STANDINGS_STATUS_FILTERS,
  STANDINGS_TITLE,
  standingsFilterEmptyLine,
  type typeStandingsRowModel,
  type typeStandingsSort,
  type typeStandingsSortKey,
  type typeStandingsStatusFilter,
} from "./poolStandingsModel";
import {
  MEMBER_ROUNDS_SHEET_NAME,
  openResultsContestSearch,
  openResultsViewSearch,
  readResultsView,
  type typeResultsView,
} from "./poolStandingsRoute";
import { PoolStandingsRow } from "./PoolStandingsRow";
import { RoundPicksList } from "./RoundPicksList";
import { UnpickedNudgeList } from "./UnpickedNudgeList";

export const RESULTS_VIEW_GROUP_LABEL = "Results view";
export const RESULTS_VIEW_OPTIONS: readonly {
  value: typeResultsView;
  label: string;
}[] = [
  { value: "standings", label: "Standings" },
  { value: "picks", label: "Picks" },
];

export const PoolStandingsPane = ({
  poolId,
  contestId,
  panel,
  canNudge = false,
  onGoBack,
  onStartContest,
}: {
  poolId: string;
  contestId?: string | undefined;
  /** The tab's own sub-address. `picks` opens the Picks view (ISSUE-1317). */
  panel?: string | null | undefined;
  /**
   * The Commissioner and Admins. Presentation only: it spares a plain member
   * a read the server would refuse. `/round/unpicked-list` is still the gate
   * on the "Still to pick" card (ISSUE-1328).
   */
  canNudge?: boolean | undefined;
  /**
   * Where a back control on this board goes, when it has one.
   *
   * Omitted when the board is the pool screen's Results TAB. A tab has
   * nothing behind it, and `STANDINGS_BACK_LABEL` promises a trip back to the
   * pool while the only route out of a tab leaves the pool altogether. The
   * pool screen's own back arrow, one row up, is the way out there.
   */
  onGoBack?: (() => void) | undefined;
  /**
   * Present only for the Commissioner and Admins. Opens the New contest
   * dialog from the no-contest state (ISSUE-1042 R4).
   */
  onStartContest?: (() => void) | undefined;
}): ReactNode => {
  const navigate = useNavigate();
  const overlay = useOverlay();
  const search = useSearch({ strict: false }) as { memberId?: string };
  const contests = useContestList({ poolId });
  const resolvedContestId = selectStandingsContestId(
    contests.data,
    contestId ?? null,
  );
  const contestOptions = buildContestOptions(contests.data?.data?.items ?? []);

  // ISSUE-802. Standings change through the server side grading job, never
  // through a mutation this app can run. So no `reactQueryDependencies`
  // entry can invalidate this read, because there is no mutation to hang
  // one off. Without an override, the app's 30 second freshness window
  // governs. A fan reopening the board then sees the pre grading order.
  // The read shortens its own freshness instead, the way
  // `poolAwards/PoolAwardsCard.tsx` already does for `awardCustomList`.
  // `useContestList` above is deliberately left on the app default: the
  // contest list is not what a grading run changes.
  const standings = useStandingsGet(
    { poolId, contestId: resolvedContestId ?? "" },
    {
      enabled: resolvedContestId !== null,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  const isFailed =
    contests.isError ||
    standings.isError ||
    extractErrorCode(contests.data) !== null ||
    (resolvedContestId !== null && extractErrorCode(standings.data) !== null);

  const state = resolveStandingsPhase({
    isLoading: contests.isLoading || standings.isLoading,
    isFailed,
    contestId: resolvedContestId,
    entries: standings.data?.data?.entries ?? null,
  });

  const view = readResultsView(panel);
  const [sort, setSort] = useState<typeStandingsSort>(DEFAULT_STANDINGS_SORT);
  const [statusFilter, setStatusFilter] =
    useState<typeStandingsStatusFilter>("all");
  // The row a sheet was opened from, so a member holding two entries sees the
  // one they tapped. Lost on a refresh, which then shows every entry.
  const [focusEntryId, setFocusEntryId] = useState<string | null>(null);

  // buildStandingsRowModels is where the board's own order and the deleted
  // account/own-row decoration happen: state.rows carries the API's raw,
  // unsorted array (see poolStandingsModel.ts). The fan's sort and filter
  // then work on that order and never change a rank.
  const rowModels =
    state.phase === "ready" ? buildStandingsRowModels(state.rows) : [];
  const shownRows = applyStandingsSort(
    filterStandingsRows(rowModels, statusFilter),
    sort,
  );

  // No check on contest kind, on purpose (ISSUE-1071). `/pool/get` types
  // `contestType` as the single value "survivor", so every pool this app can
  // open is a Survivor pool. The day a second contest type joins that enum,
  // the member sheet needs a gate, because `/results/get-by-member` and the
  // words Skipped and Missed are Survivor's.
  const sheetMemberId =
    overlay.isOpen("sheet", MEMBER_ROUNDS_SHEET_NAME) &&
    typeof search.memberId === "string"
      ? search.memberId
      : null;
  const sheetRows =
    sheetMemberId === null
      ? []
      : rowModels.filter((row) => row.userId === sheetMemberId);
  const sheetRow =
    sheetRows.find((row) => row.entryId === focusEntryId) ??
    (sheetRows.length === 1 ? sheetRows[0] : undefined) ??
    null;

  function openMember(row: typeStandingsRowModel): void {
    if (row.userId === undefined || row.userId === "") return;
    setFocusEntryId(row.entryId);
    overlay.open({
      kind: "sheet",
      name: MEMBER_ROUNDS_SHEET_NAME,
      params: { memberId: row.userId },
    });
  }

  function openMemberByEntry(entryId: string): void {
    const row = rowModels.find((candidate) => candidate.entryId === entryId);
    if (row !== undefined) openMember(row);
  }

  return (
    <div className="flex flex-col gap-3" data-testid="pool-standings-pane">
      <div
        className="flex items-center gap-2"
        data-testid="pool-standings-header"
      >
        {onGoBack === undefined ? null : (
          <Button variant="ghost" size="sm" onClick={onGoBack}>
            {STANDINGS_BACK_LABEL}
          </Button>
        )}
        <h2 className="text-h2 text-ink">{STANDINGS_TITLE}</h2>
      </div>
      {contestOptions.length > 1 && resolvedContestId !== null ? (
        <div data-testid="pool-standings-contest">
          <Select
            label={STANDINGS_CONTEST_LABEL}
            items={contestOptions}
            value={resolvedContestId}
            name="pool-standings-contest"
            onValueChange={(next) => {
              void navigate({
                to: ".",
                replace: true,
                search: (previous: unknown) =>
                  openResultsContestSearch(
                    (previous ?? {}) as Record<string, unknown>,
                    next,
                  ),
              });
            }}
          />
        </div>
      ) : null}
      {state.phase === "loading" ? (
        <div data-testid="pool-standings-loading">
          <Skeleton shape="card" label={STANDINGS_LOADING_LABEL} />
          <Skeleton shape="card" />
          <Skeleton shape="card" />
        </div>
      ) : null}
      {state.phase === "failed" ? (
        <div data-testid="pool-standings-failed">
          <InlineBanner
            variant="warning"
            message={STANDINGS_FAILED_MESSAGE}
            action={{
              label: STANDINGS_RETRY_LABEL,
              onAction: () => {
                void contests.refetch();
                void standings.refetch();
              },
            }}
          />
        </div>
      ) : null}
      {state.phase === "noContest" ? (
        <div data-testid="pool-standings-no-contest">
          <SectionEmptyState
            headline={STANDINGS_NO_CONTEST_HEADLINE}
            body={STANDINGS_NO_CONTEST_BODY}
          />
          {onStartContest !== undefined ? (
            <div className="flex justify-center">
              <Button
                variant="primary"
                onClick={onStartContest}
                data-testid="pool-standings-start-contest"
              >
                {NEW_CONTEST_START_ACTION_LABEL}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {canNudge && resolvedContestId !== null ? (
        <UnpickedNudgeList poolId={poolId} contestId={resolvedContestId} />
      ) : null}
      {resolvedContestId !== null &&
      (state.phase === "ready" || state.phase === "empty") ? (
        <SegmentedControl
          label={RESULTS_VIEW_GROUP_LABEL}
          options={RESULTS_VIEW_OPTIONS}
          value={view}
          fullWidth
          testId="pool-results-view"
          onValueChange={(next) => {
            void navigate({
              to: ".",
              replace: true,
              search: (previous: unknown) =>
                openResultsViewSearch(
                  (previous ?? {}) as Record<string, unknown>,
                  next === "picks" ? "picks" : "standings",
                ),
            });
          }}
        />
      ) : null}
      {view === "picks" &&
      resolvedContestId !== null &&
      (state.phase === "ready" || state.phase === "empty") ? (
        <RoundPicksList
          poolId={poolId}
          contestId={resolvedContestId}
          onOpenMember={openMemberByEntry}
        />
      ) : null}
      {view === "standings" && state.phase === "empty" ? (
        <div data-testid="pool-standings-empty">
          <SectionEmptyState
            headline={STANDINGS_EMPTY_HEADLINE}
            body={STANDINGS_EMPTY_BODY}
          />
        </div>
      ) : null}
      {view === "standings" && state.phase === "ready" ? (
        <div className="flex flex-col gap-3">
          <StatusFilterChips value={statusFilter} onChange={setStatusFilter} />
          <div>
            <SortHeader
              sort={sort}
              showRevived={hasAnyRevive(state.rows)}
              onSort={(key) => {
                setSort((current) => nextStandingsSort(current, key));
              }}
            />
            {shownRows.length === 0 ? (
              <p
                className="border-t border-hairline px-4 py-4 text-body text-ink-secondary"
                data-testid="pool-standings-filtered-empty"
              >
                {standingsFilterEmptyLine(statusFilter)}
              </p>
            ) : (
              <ul data-testid="pool-standings-board">
                {shownRows.map((row) => (
                  <PoolStandingsRow
                    key={row.entryId}
                    row={row}
                    onOpen={openMember}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
      {resolvedContestId !== null &&
      sheetMemberId !== null &&
      sheetRows.length > 0 ? (
        <MemberRoundsSheet
          open
          onOpenChange={(open) => {
            if (!open) {
              setFocusEntryId(null);
              overlay.close({ kind: "sheet", clearParams: ["memberId"] });
            }
          }}
          poolId={poolId}
          contestId={resolvedContestId}
          targetUserId={sheetMemberId}
          displayName={sheetRow?.displayName ?? sheetRows[0]?.displayName ?? ""}
          focusEntryId={sheetRow?.entryId ?? null}
          wins={sheetRow?.wins ?? null}
          losses={sheetRow?.losses ?? null}
        />
      ) : null}
    </div>
  );
};

/** All, In, Revived, Eliminated, the legacy board's status pills. */
function StatusFilterChips(props: {
  value: typeStandingsStatusFilter;
  onChange: (next: typeStandingsStatusFilter) => void;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label={STANDINGS_FILTER_GROUP_LABEL}
      className="flex flex-wrap gap-2"
      data-testid="pool-standings-filter"
    >
      {STANDINGS_STATUS_FILTERS.map((filter) => {
        const isActive = filter.value === props.value;
        return (
          <Button
            key={filter.value}
            type="button"
            size="sm"
            className="rounded-full"
            variant={isActive ? "primary" : "secondary"}
            aria-pressed={isActive}
            data-testid={`pool-standings-filter-${filter.value}`}
            onClick={() => {
              props.onChange(filter.value);
            }}
          >
            {filter.label}
          </Button>
        );
      })}
    </div>
  );
}

const SORT_ARROW = { asc: "\u2191", desc: "\u2193" } as const;

function SortButton(props: {
  sortKey: typeStandingsSortKey;
  sort: typeStandingsSort;
  onSort: (key: typeStandingsSortKey) => void;
  className?: string;
}): ReactNode {
  const isActive = props.sort.key === props.sortKey;
  return (
    <button
      type="button"
      className={cn(
        "flex min-h-touch-target min-w-touch-target items-center gap-1 text-caption",
        "transition-colors duration-200 ease-standard motion-reduce:transition-none",
        "focus-visible:outline-2 focus-visible:outline-focus-ring",
        isActive ? "text-ink" : "text-ink-secondary hover:text-ink",
        props.className,
      )}
      aria-pressed={isActive}
      data-testid={`pool-standings-sort-${props.sortKey}`}
      onClick={() => {
        props.onSort(props.sortKey);
      }}
    >
      {STANDINGS_SORT_LABELS[props.sortKey]}
      {isActive ? (
        <span aria-hidden="true">{SORT_ARROW[props.sort.direction]}</span>
      ) : null}
    </button>
  );
}

/**
 * The board's column headers. Tapping one sorts by it; tapping it again flips
 * the order (ISSUE-1348). The widths match `PoolStandingsRow` so each header
 * sits over its own column: Rank spans the rank and the photo (24 + 12 + 32,
 * or 24 + 8 + 32 below sm, where the row gap tightens to 8 to give the name
 * column room on a 320 phone), Player fills the name column, and Wins and Pts
 * are the row's two 44px number columns.
 *
 * Medals and Revived have no column of their own (a third number column does
 * not fit at 320px); their counts sit on the stats line under the name, so
 * their headers belong to the name column too (ISSUE-1327). From sm up they
 * sit right aligned in the name column, beside Player. Below sm the name
 * column is only about 80px, too narrow for Player, Medals and Revived side
 * by side, and squeezing them in let their 44px tap targets run into Wins
 * (ISSUE-1348 round 5). So below sm they move to a second header line that
 * starts where the name column starts, the way the stats line starts under
 * the name in each row. The line is not held to the name column's width:
 * Medals and Revived together need about 108px (about 124px once one of them
 * shows the sort arrow), more than the 80 to 120px name column at 320 and
 * 360, so Revived can run on under the Wins header. It sits a full line below
 * Wins, so no tap target overlaps. Wrapping Revived to a third line instead
 * would only happen once an arrow appears, shifting the board on a tap. Every
 * button keeps a 44px tap target that overlaps no other.
 */
function SortHeader(props: {
  sort: typeStandingsSort;
  showRevived: boolean;
  onSort: (key: typeStandingsSortKey) => void;
}): ReactNode {
  return (
    <div
      role="group"
      aria-label={STANDINGS_SORT_GROUP_LABEL}
      className="flex flex-wrap items-center gap-x-2 px-4 sm:gap-x-3"
      data-testid="pool-standings-sort"
    >
      <SortButton
        sortKey="rank"
        sort={props.sort}
        onSort={props.onSort}
        className="w-16 shrink-0 sm:w-17"
      />
      <SortButton
        sortKey="name"
        sort={props.sort}
        onSort={props.onSort}
        className="flex-1"
      />
      {/* Below sm: last in order and a full line of its own, indented by the
          rank column plus its gap (64 + 8) so Medals starts under Player.
          From sm up: back between Player and Wins, right aligned in the name
          column because Player takes the free width. */}
      <div
        className="order-last flex basis-full items-center gap-2 pl-18 sm:order-none sm:basis-auto sm:pl-0"
        data-testid="pool-standings-sort-name-extras"
      >
        <SortButton sortKey="medals" sort={props.sort} onSort={props.onSort} />
        {props.showRevived ? (
          <SortButton
            sortKey="revived"
            sort={props.sort}
            onSort={props.onSort}
          />
        ) : null}
      </div>
      <SortButton
        sortKey="wins"
        sort={props.sort}
        onSort={props.onSort}
        className="w-11 shrink-0 justify-end"
      />
      <SortButton
        sortKey="points"
        sort={props.sort}
        onSort={props.onSort}
        className="w-11 shrink-0 justify-end"
      />
    </div>
  );
}
